<template>
  <section class="c_video_embed" :style="setVideoEmbedStyles()">
    <figure>
      <div :class="embedContainerWidth">
        <div class="c_video_embed__thumbnail_container" v-if="!showVideo">
          <MediaImage
            :sources="posterSources"
            v-if="posterSources && posterSources.length && posterSources[0] !== undefined"
            class="c_video_embed__thumbnail"
          />
          <div v-if="video.videoDuration" class="c_video_embed__duration">
            {{ video.videoDuration }}
          </div>
          <button @click="playVideo()" :aria-label="$t('Media - Play')" aria-controls="videoEmbed">
            <div class="c_video_embed__play">
              <IconSVG handle="hotspot_play" width="20" height="20" />
            </div>
          </button>
        </div>
        <div id="videoEmbed" aria-live="polite">
          <div v-if="showVideo" class="c_video_embed__video">
            <MediaVideo
              v-if="mediaType === 'youtube'"
              source="youtube"
              :video-id="data.video[0].videoId"
              :poster="posterSources?.[0]?.srcWebp"
              autoplay
              playsinline
            />
            <video v-if="mediaType === 'file'" class="" controls autoplay playsinline>
              <source :src="video.url" type="video/mp4" :poster="posterSources?.[0]?.srcWebp" />
            </video>
          </div>
        </div>
      </div>
      <figcaption v-if="video.title || video.videoDescription" class="c_wrapper c_text">
        <h4 class="c_header c_header--3 c_text--graphik_bold" v-if="video.title">{{ video.title }}</h4>
        <p v-html="video.videoDescription" v-if="video.videoDescription"></p>
      </figcaption>
    </figure>
  </section>
</template>

<script>
import { defineComponent, reactive, toRefs, ref } from '@nuxtjs/composition-api';
import MediaVideo from 'Components/video/MediaVideo.vue';
import MediaImage from 'Components/image/MediaImage.vue';
import IconSVG from 'Components/icon_svg/IconSVG.vue';

export default defineComponent({
  name: 'VideoEmbed',
  props: {
    data: { type: Object, required: true },
  },
  components: {
    MediaVideo,
    MediaImage,
    IconSVG,
  },
  setup(props) {
    const state = reactive({
      mediaType: 'none',
      video: ref(null),
      posterSources: [],
      showVideo: false,
      embedContainerWidth: 'c_wrapper',
      captionColor: 'var(--color-black)',
    });

    state.video = props?.data?.video?.[0];

    // determine file type by asset volume
    state.mediaType = state.video.__typename === 'videos_Asset' ? 'youtube' : 'file';

    // determine source location by media type
    state.posterSources = state.mediaType === 'file' ? [state.video?.videoThumbnail?.[0]?.image] : [state.video?.image];

    // assign wrapper class based on `embedStyle` field
    state.embedContainerWidth = props.data.embedStyle === 'fitPage' ? 'c_wrapper' : 'c_wrapper_wide';

    // set caption color css variable based on `captionColor` field
    state.captionColor = props.data.captionColor === 'black' ? 'var(--color-black)' : 'var(--color-gray_86)';

    // handler to hide thumbnail and play video
    const playVideo = () => {
      state.showVideo = true;
    };

    // set css variables for theming on parent object
    const setVideoEmbedStyles = () => {
      return {
        '--caption-color': state.captionColor,
      };
    };

    return { ...toRefs(state), playVideo, setVideoEmbedStyles };
  },
});
</script>

<style lang="scss" scoped>
.c_video_embed {
  // gets overwritten by `captionColor` field
  --caption-color: var(--color-black);

  figure {
    line-height: 1;
  }
  figcaption {
    margin-top: px_to_rem(30);
    color: var(--caption-color);
  }
  .c_video_embed__thumbnail_container {
    aspect-ratio: 16/9;
    object-fit: cover;
    object-position: center center;
    position: relative;
    background-color: $color_black;
    overflow: hidden;
    button {
      all: unset;
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      margin: auto;
      width: 100%;
      height: 100%;
      display: flex;
      justify-content: center;
      align-items: center;
      background-color: rgba(0, 0, 0, 0.25);
      transition:
        background-color,
        ease-in-out 0.15s;
      will-change: background-color;
      cursor: pointer;
      &:hover {
        background-color: rgba(0, 0, 0, 0.05);
        .c_video_embed__play {
          background-color: $color_red_255;
        }
      }
      .c_video_embed__play {
        width: px_to_rem(60);
        height: px_to_rem(60);
        background-color: $color_red;
        border-radius: 50%;
        display: flex;
        justify-content: center;
        align-items: center;
        color: $color_white;
        transition:
          background-color,
          ease-in-out 0.15s;
        will-change: background-color;
        @include mq($mq_sm_to_md) {
          width: px_to_rem(80);
          height: px_to_rem(80);
        }
      }
    }
  }
  .c_video_embed__duration {
    position: absolute;
    pointer-events: none;
    user-select: none;
    bottom: 0;
    right: 0;
    background-color: rgba(0, 0, 0, 0.75);
    color: $color_white;
    font-size: px_to_rem(14);
    line-height: px_to_rem(14);
    padding: px_to_rem(10) px_to_rem(24);
    z-index: 50;
    @include font('graphik_regular');
    @include mq($mq_sm_to_md) {
      font-size: px_to_rem(18);
      line-height: px_to_rem(18);
    }
  }
  .c_video_embed__video {
    background-color: $color_black;
  }
  h4 {
    margin-bottom: px_to_rem(10);
    line-height: px_to_rem(18);
  }
  p {
    @include font('graphik_regular');
  }
}
</style>
